import React, {useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {fetchAllTimeLeaderboardAll} from '../lib/api';
import {formatLeaderboardDate} from '../lib/utils';
import {LeaderboardScore, CATEGORIES, ScoreCategory} from '../types/leaderboard';
import '../styles/ranking.css';

function dedupeByUser(scores: LeaderboardScore[]): LeaderboardScore[] {
  const bestByUser = new Map<string, LeaderboardScore>();

  for (const s of scores) {
    const key = s.user_id || s.display_name;
    const existing = bestByUser.get(key);
    if (!existing || s.score > existing.score) {
      bestByUser.set(key, s);
    }
  }

  const unique = Array.from(bestByUser.values());
  unique.sort((a, b) => b.score - a.score);
  return unique;
}

const CategoryRanking: React.FC = () => {
  const params = useParams<{ categoryId: string }>();
  const categoryId = params.categoryId || '';

  const categoryKey = useMemo(() => {
    const keys = Object.keys(CATEGORIES) as ScoreCategory[];
    return keys.find((k) => k === categoryId) as ScoreCategory | undefined;
  }, [categoryId]);

  const [scores, setScores] = useState<LeaderboardScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!categoryKey) {
      setError('Unknown category');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetchAllTimeLeaderboardAll();
        const rawScores = res.scoresByCategory[categoryKey] || [];
        const unique = dedupeByUser(rawScores);
        const trimmed = unique.slice(0, 30); // top 30 from up to 50
        setScores(trimmed);
      } catch (err: any) {
        setError(err?.message || 'Failed to load category leaderboard. See console for details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [categoryKey]);

  if (!categoryKey) {
    return (
      <div className="ranking-page">
        <h1 className="page-title">Unknown category</h1>
        {error && <div className="error-text">Error: {error}</div>}
      </div>
    );
  }

  const categoryName = CATEGORIES[categoryKey].label;

  return (
    <div className="ranking-page">
      <Link to="/ranking" className="back-link secondary-link">← Back to Weekly Rankings</Link>
      <Link to="/ranking-global" className="back-link secondary-link">← Back to Global Rankings</Link>
      <h1 className="page-title">Global Ranking - {categoryName}</h1>
      <p className="page-subtitle">Top 30 players in this category (each player appears at most once).</p>

      {loading && <div>Loading...</div>}
      {error && <div className="error-text">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrapper">
          <table className="ranking-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th>Score</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {scores.length === 0 && (<tr><td colSpan={4}>No global rankings available for this category yet.</td></tr>)}
              {scores.map((p, index) => (
                <tr key={`${p.user_id}-${p.at}`}>
                  <td>{index + 1}</td>
                  <td><Link to={`/profile/${encodeURIComponent(p.display_name)}`} className="player-link">{p.display_name}</Link></td>
                  <td>{p.score.toLocaleString()}</td>
                  <td>{formatLeaderboardDate(p.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CategoryRanking;
